import { IAircraftGroup } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import {
  FLIGHT_PAIRING_STATUS_CATALOGUE,
  FLIGHT_PAIRING_STATUS_ORDER,
  FlightPairingStatusCode,
  IEpochRange,
  IFlightPairingStatusMeta,
  IFlightPairingStatusRow,
  PROBLEM_STATUS_CODES,
  routeKeyOf,
} from "./pairingStatusTypes";

/**
 * Pure helpers for the Flight Pairing Status window: filtering, grouping and counting the cached rows.
 * Everything here runs on the client against the loaded range, so every filter is instant.
 */

/** The value filters that can be flipped from "is one of" to "is not one of". */
export type NegatableFilterKey =
  | "statuses"
  | "origins"
  | "destinations"
  | "flightTypeIds"
  | "aircraftGroups"
  | "positions";

export interface IPairingStatusFilters {
  /** Free text: flight number, route, aircraft registration or pairing code. A `-token` must NOT match. */
  search: string;
  /** Empty = every status. */
  statuses: FlightPairingStatusCode[];
  origins: string[];
  destinations: string[];
  flightTypeIds: string[];
  aircraftGroups: IAircraftGroup[];
  /** Position ids and/or titles; a flight matches when the position is on a linked pairing or is missing. */
  positions: { _id: string; title: string }[];
  includeSubAircraft: boolean;
  problemsOnly: boolean;
  /**
   * Per value filter: true turns "keep flights matching any selected value" into "drop flights matching any
   * selected value" (e.g. every flight type except Training). A negated filter with no values is inert.
   */
  negate: Partial<Record<NegatableFilterKey, boolean>>;
}

export const emptyPairingStatusFilters: IPairingStatusFilters = {
  search: "",
  statuses: [],
  origins: [],
  destinations: [],
  flightTypeIds: [],
  aircraftGroups: [],
  positions: [],
  includeSubAircraft: false,
  problemsOnly: false,
  negate: {},
};

export const isNegated = (filters: IPairingStatusFilters, key: NegatableFilterKey): boolean =>
  filters.negate?.[key] === true;

export const hasActiveFilters = (filters: IPairingStatusFilters): boolean =>
  filters.search.trim().length > 0 ||
  filters.statuses.length > 0 ||
  filters.origins.length > 0 ||
  filters.destinations.length > 0 ||
  filters.flightTypeIds.length > 0 ||
  filters.aircraftGroups.length > 0 ||
  filters.positions.length > 0 ||
  filters.problemsOnly;

export interface ISearchToken {
  text: string;
  /** Written as `-text`: the row must NOT match it. */
  exclude: boolean;
}

export const searchTokens = (search: string): ISearchToken[] =>
  search
    .toUpperCase()
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .map((t) => (t.startsWith("-") ? { text: t.slice(1), exclude: true } : { text: t, exclude: false }))
    .filter((t) => t.text.length > 0);

/** Searchable text for a row: route, registration and pairing codes (flight numbers are matched by prefix). */
export const searchTextOf = (row: IFlightPairingStatusRow): string =>
  [
    row.origin,
    row.destination,
    `${row.origin}-${row.destination}`,
    row.aircraft?.registration ?? "",
    ...(row.pairingShortCodes ?? []),
  ]
    .join(" ")
    .toUpperCase();

/** A token matches a row when it is a prefix of the flight number (as the legacy filter did) or part of the text. */
export const tokenMatchesRow = (token: string, row: IFlightPairingStatusRow, text: string): boolean =>
  `${row.flightNumber}${row.flightNumberSuffix ?? ""}`.toUpperCase().startsWith(token) || text.includes(token);

/**
 * Apply one value filter: with no values it passes everything; otherwise the row must match one of the values
 * ("is") or none of them ("is not").
 */
const passesValueFilter = (hasValues: boolean, matches: boolean, negated: boolean): boolean =>
  !hasValues || (negated ? !matches : matches);

export const rowMatchesFilters = (
  row: IFlightPairingStatusRow,
  filters: IPairingStatusFilters,
  tokens: ISearchToken[] = searchTokens(filters.search)
): boolean => {
  if (!filters.includeSubAircraft && (row.aircraft?.planning || row.aircraft?.subCharter)) return false;
  if (filters.problemsOnly && !PROBLEM_STATUS_CODES.includes(row.status?.code)) return false;
  if (
    !passesValueFilter(
      filters.statuses.length > 0,
      filters.statuses.includes(row.status?.code),
      isNegated(filters, "statuses")
    )
  ) {
    return false;
  }
  if (
    !passesValueFilter(filters.origins.length > 0, filters.origins.includes(row.origin), isNegated(filters, "origins"))
  ) {
    return false;
  }
  if (
    !passesValueFilter(
      filters.destinations.length > 0,
      filters.destinations.includes(row.destination),
      isNegated(filters, "destinations")
    )
  ) {
    return false;
  }
  if (
    !passesValueFilter(
      filters.flightTypeIds.length > 0,
      filters.flightTypeIds.includes(row.flightType?._id ?? ""),
      isNegated(filters, "flightTypeIds")
    )
  ) {
    return false;
  }
  if (filters.aircraftGroups.length > 0) {
    const aircraftId = row.aircraft?._id;
    const inGroup = !!aircraftId && filters.aircraftGroups.some((group) => (group.members ?? []).includes(aircraftId));
    if (!passesValueFilter(true, inGroup, isNegated(filters, "aircraftGroups"))) return false;
  }
  if (filters.positions.length > 0) {
    const onPairing = new Set((row.actualCrew ?? []).map((c) => c.posId ?? ""));
    const titles = new Set([
      ...(row.actualCrew ?? []).map((c) => c.pos),
      ...(row.crewSummary?.missing ?? []).map((m) => m.positionCode),
      ...(row.crewSummary?.extraCrew ?? []).map((m) => m.positionCode),
      ...(row.crewSummary?.extraPositions ?? []).map((m) => m.positionCode),
    ]);
    const involvesPosition = filters.positions.some((p) => onPairing.has(p._id) || titles.has(p.title));
    if (!passesValueFilter(true, involvesPosition, isNegated(filters, "positions"))) return false;
  }
  if (tokens.length > 0) {
    const text = searchTextOf(row);
    if (!tokens.every((token) => tokenMatchesRow(token.text, row, text) !== token.exclude)) return false;
  }
  return true;
};

export const filterRows = (
  rows: IFlightPairingStatusRow[],
  filters: IPairingStatusFilters
): IFlightPairingStatusRow[] => {
  const tokens = searchTokens(filters.search);
  return rows.filter((row) => rowMatchesFilters(row, filters, tokens));
};

export type StatusCounts = Record<FlightPairingStatusCode, number>;

export const emptyStatusCounts = (): StatusCounts =>
  FLIGHT_PAIRING_STATUS_ORDER.reduce((acc, code) => ({ ...acc, [code]: 0 }), {} as StatusCounts);

export const countByStatus = (rows: IFlightPairingStatusRow[]): StatusCounts => {
  const counts = emptyStatusCounts();
  for (const row of rows) {
    const code = row.status?.code;
    if (code && code in counts) counts[code] += 1;
  }
  return counts;
};

/** Sort key for a row inside a day cell: most severe status first, then by departure time. */
export const compareRowsForDisplay = (a: IFlightPairingStatusRow, b: IFlightPairingStatusRow): number => {
  const pa = FLIGHT_PAIRING_STATUS_CATALOGUE[a.status?.code]?.priority ?? 99;
  const pb = FLIGHT_PAIRING_STATUS_CATALOGUE[b.status?.code]?.priority ?? 99;
  if (pa !== pb) return pa - pb;
  return new Date(a.scheduledOut).valueOf() - new Date(b.scheduledOut).valueOf();
};

export const compareRowsByDeparture = (a: IFlightPairingStatusRow, b: IFlightPairingStatusRow): number =>
  new Date(a.scheduledOut).valueOf() - new Date(b.scheduledOut).valueOf() ||
  a.flightNumber - b.flightNumber ||
  a.origin.localeCompare(b.origin);

export interface IDayColumn {
  /** YYYY-MM-DD (UTC) */
  key: string;
  /** Day of month, no leading zero. */
  label: string;
  /** e.g. "Tue 07 Oct 2026" */
  description: string;
  isWeekend: boolean;
  isToday: boolean;
}

/** One column per UTC calendar day in the range (inclusive). */
export const buildDayColumns = (range: IEpochRange, now: number = Date.now()): IDayColumn[] => {
  const columns: IDayColumn[] = [];
  const todayKey = moment.utc(now).format("YYYY-MM-DD");
  const cursor = moment.utc(range.start).startOf("day");
  const last = moment.utc(range.end).startOf("day");
  // Hard stop so a bad range can never build an unbounded column set.
  for (let i = 0; i < 400 && !cursor.isAfter(last); i++) {
    const key = cursor.format("YYYY-MM-DD");
    columns.push({
      key,
      label: String(cursor.date()),
      description: cursor.format("ddd DD MMM YYYY"),
      isWeekend: cursor.isoWeekday() >= 6,
      isToday: key === todayKey,
    });
    cursor.add(1, "day");
  }
  return columns;
};

export interface IRouteRow {
  /** flightNumber|origin|destination */
  id: string;
  flightNumber: number;
  origin: string;
  destination: string;
  /** Flights per day column key, most severe first. */
  days: Record<string, IFlightPairingStatusRow[]>;
  /** Largest number of flights on any single day (drives the row height). */
  maxPerDay: number;
  /** Most severe status priority across the row (for sorting rows by trouble). */
  worstPriority: number;
  flightCount: number;
}

/** Group flights into calendar rows: one row per flight number + routing, one cell per day. */
export const groupRowsByRoute = (rows: IFlightPairingStatusRow[]): IRouteRow[] => {
  const byRoute = new Map<string, IRouteRow>();
  for (const row of rows) {
    const key = routeKeyOf(row);
    let route = byRoute.get(key);
    if (!route) {
      route = {
        id: key,
        flightNumber: row.flightNumber,
        origin: row.origin,
        destination: row.destination,
        days: {},
        maxPerDay: 0,
        worstPriority: 99,
        flightCount: 0,
      };
      byRoute.set(key, route);
    }
    const dayRows = route.days[row.dayKey] ?? (route.days[row.dayKey] = []);
    dayRows.push(row);
    route.flightCount += 1;
    route.maxPerDay = Math.max(route.maxPerDay, dayRows.length);
    const priority = FLIGHT_PAIRING_STATUS_CATALOGUE[row.status?.code]?.priority ?? 99;
    route.worstPriority = Math.min(route.worstPriority, priority);
  }
  for (const route of byRoute.values()) {
    for (const dayRows of Object.values(route.days)) dayRows.sort(compareRowsForDisplay);
  }
  return [...byRoute.values()].sort(
    (a, b) =>
      a.flightNumber - b.flightNumber || a.origin.localeCompare(b.origin) || a.destination.localeCompare(b.destination)
  );
};

/** Distinct option values present in the loaded rows (for the origin/destination selects). */
export const distinctAirports = (rows: IFlightPairingStatusRow[]): { origins: string[]; destinations: string[] } => {
  const origins = new Set<string>();
  const destinations = new Set<string>();
  for (const row of rows) {
    if (row.origin) origins.add(row.origin);
    if (row.destination) destinations.add(row.destination);
  }
  return { origins: [...origins].sort(), destinations: [...destinations].sort() };
};

/** Default range: the current UTC month. */
export const currentMonthRange = (now: number = Date.now()): IEpochRange => ({
  start: moment.utc(now).startOf("month").valueOf(),
  end: moment.utc(now).endOf("month").valueOf(),
});

export const shiftRangeByMonths = (range: IEpochRange, months: number): IEpochRange => {
  const start = moment.utc(range.start).startOf("month").add(months, "month");
  return { start: start.valueOf(), end: moment(start).endOf("month").valueOf() };
};

/**
 * Step a range forward or back: a whole month moves by a month, a custom range moves by its own length so the
 * user's choice of span is kept.
 */
export const stepRange = (range: IEpochRange, direction: 1 | -1): IEpochRange => {
  if (isFullMonthRange(range)) return shiftRangeByMonths(range, direction);
  const days = Math.max(1, Math.round((range.end + 1 - range.start) / 86_400_000));
  const start = moment
    .utc(range.start)
    .startOf("day")
    .add(direction * days, "day");
  return {
    start: start.valueOf(),
    end: moment(start)
      .add(days - 1, "day")
      .endOf("day")
      .valueOf(),
  };
};

/** The UTC day keys (YYYY-MM-DD) that bound a range, matching how the server selects rows. */
export const dayKeyRange = (range: IEpochRange): { start: string; end: string } => ({
  start: moment.utc(range.start).format("YYYY-MM-DD"),
  end: moment.utc(range.end).format("YYYY-MM-DD"),
});

/**
 * The evaluation the stale warning judges: the oldest among the rows on days the server's scheduled refresh keeps
 * fresh (`meta.scheduledWindow`). Rows on other days are re-evaluated only when something about them changes, so
 * their age says nothing about whether the refresh is running. Null when no such row is held or the schedule does not
 * run in this environment; a server that does not report its window falls back to the oldest evaluation in the range.
 */
export const oldestScheduledEvaluation = (
  rows: IFlightPairingStatusRow[],
  meta: IFlightPairingStatusMeta | null
): number | null => {
  if (!meta) return null;
  if (meta.scheduledWindow === undefined) return meta.oldestComputedAt ?? meta.lastComputedAt;
  if (meta.scheduledWindow === null) return null;
  const days = dayKeyRange(meta.scheduledWindow);
  let oldest = Infinity;
  for (const row of rows) {
    if (row.dayKey < days.start || row.dayKey > days.end) continue;
    const ms = Date.parse(row.computedAt);
    if (ms < oldest) oldest = ms;
  }
  return Number.isFinite(oldest) ? oldest : null;
};

export const isFullMonthRange = (range: IEpochRange): boolean => {
  const start = moment.utc(range.start);
  const end = moment.utc(range.end);
  return (
    start.isSame(moment(start).startOf("month")) &&
    end.isSame(moment(end).endOf("month"), "minute") &&
    start.isSame(end, "month")
  );
};

export const describeRange = (range: IEpochRange): string => {
  const start = moment.utc(range.start);
  const end = moment.utc(range.end);
  if (isFullMonthRange(range)) return start.format("MMMM YYYY");
  return `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`;
};

/** Human readable age, e.g. "just now", "4 min ago", "2 h ago". */
export const describeAge = (epoch: number | null | undefined, now: number = Date.now()): string => {
  if (!epoch) return "never";
  const seconds = Math.max(0, Math.round((now - epoch) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
};
